import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useUserAuthOptional } from '../contexts/UserAuthContext';
import {
  AMBIGUOUS_MESSAGE,
  CONFLICT_MESSAGE,
  DEFAULT_RADIUS_KM,
  REJECTED_MESSAGE,
  syncPersonProperties,
  type PrefsSyncNotice,
} from './useUserPrefsSync';
import {
  claimV2AnonStored,
  loadV2Stored,
  saveV2Stored,
  V2_LOCAL_ONLY_PROVENANCE,
  type V2PrefsPatch,
} from './prefsV2Storage';
import type { UserPreferencesV2 } from '@buzzmap/shared';

/**
 * Server synchronisation for the v2 preference protocol (T011-FE).
 *
 * Wire contract (T008-BE): `GET/PUT /api/me/preferences` with
 * `municipalityId` + `locationNeedsConfirmation`, optimistic concurrency via
 * `ifRevision`, 409 → adopt `body.current`, 422 → show `candidates` (never
 * guess), 503 → the intent survives for the next trigger. The response
 * `city`/`province` labels are derived display data and are deliberately NOT
 * read here — a name is never a second write authority.
 *
 * Runs ONLY while the discovery gate is open (`enabled`), so the v1 protocol
 * (`useUserPrefsSync`) and this one never speak at once: switching accounts or
 * being offline can therefore never replay a v1 geography (T011 simulation d).
 * Outbox and account isolation live in the SEPARATE v2 namespace
 * (`buzzmap_prefs_v2:*`).
 */

export interface ServerPrefsV2 {
  municipalityId: string | null;
  locationNeedsConfirmation: boolean;
  /**
   * DERIVED current label. Read ONLY for display/analytics (PostHog person
   * properties): it is never a write authority and never a location source —
   * the location is the UUID above (T008-BE).
   */
  city: string | null;
  radiusKm: number | null;
  favoriteCategories: string[];
  initialized: { city: boolean; radiusKm: boolean; favoriteCategories: boolean };
  revision: string;
  /** False when the deployment does not expose the id columns at all. */
  hasMunicipalityId: boolean;
}

export function parseServerPrefsV2(raw: unknown): ServerPrefsV2 | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if (o.radiusKm !== null && typeof o.radiusKm !== 'number') return null;
  if (o.city !== null && typeof o.city !== 'string') return null;
  if (!Array.isArray(o.favoriteCategories)) return null;
  if (!o.favoriteCategories.every((c) => typeof c === 'string')) return null;
  if (typeof o.revision !== 'string') return null;
  const init = o.initialized;
  if (!init || typeof init !== 'object') return null;
  const i = init as Record<string, unknown>;
  if (
    typeof i.city !== 'boolean' ||
    typeof i.radiusKm !== 'boolean' ||
    typeof i.favoriteCategories !== 'boolean'
  )
    return null;
  const hasMunicipalityId = Object.prototype.hasOwnProperty.call(o, 'municipalityId');
  if (hasMunicipalityId && o.municipalityId !== null && typeof o.municipalityId !== 'string')
    return null;
  const hasFlag = Object.prototype.hasOwnProperty.call(o, 'locationNeedsConfirmation');
  if (hasFlag && typeof o.locationNeedsConfirmation !== 'boolean') return null;
  return {
    municipalityId: hasMunicipalityId ? ((o.municipalityId as string | null) ?? null) : null,
    locationNeedsConfirmation: hasFlag ? (o.locationNeedsConfirmation as boolean) : false,
    city: (o.city as string | null) ?? null,
    radiusKm: o.radiusKm as number | null,
    favoriteCategories: [...(o.favoriteCategories as string[])],
    initialized: {
      city: i.city,
      radiusKm: i.radiusKm,
      favoriteCategories: i.favoriteCategories,
    },
    revision: o.revision,
    hasMunicipalityId,
  };
}

/**
 * Merge authoritative server state into the local v2 record.
 *
 * - independent fields follow the v1 rules: an initialized account field wins
 *   (including null/[]), a queued intent is never clobbered;
 * - the location is UUID-only: a server `locationNeedsConfirmation` means the
 *   identity is NULL by construction (plan l.79) — radius and favorites are
 *   kept and no territory is activated;
 * - a deployment without the id columns never clears the local location.
 */
export function applyServerPrefsV2(
  server: ServerPrefsV2,
  local: UserPreferencesV2,
  pending: V2PrefsPatch | null,
  localNeedsConfirmation: boolean
): { prefs: UserPreferencesV2; locationNeedsConfirmation: boolean } {
  const hasPending = (key: keyof V2PrefsPatch): boolean =>
    pending != null && Object.prototype.hasOwnProperty.call(pending, key);

  const next: UserPreferencesV2 = { ...local };

  if (!hasPending('favoriteCategories') && server.initialized.favoriteCategories) {
    next.favoriteCategories = [...server.favoriteCategories];
  }
  if (!hasPending('radiusKm') && server.initialized.radiusKm) {
    next.radiusKm = server.radiusKm ?? DEFAULT_RADIUS_KM;
  }

  let needsConfirmation = localNeedsConfirmation;

  if (hasPending('municipalityId') || !server.hasMunicipalityId) {
    return { prefs: next, locationNeedsConfirmation: needsConfirmation };
  }

  if (server.locationNeedsConfirmation) {
    next.location = { type: 'none' };
    return { prefs: next, locationNeedsConfirmation: true };
  }
  if (!server.initialized.city) {
    // The account never wrote a location: the local choice (and its
    // confirmation state) stays until an explicit intent lands.
    return { prefs: next, locationNeedsConfirmation: needsConfirmation };
  }
  next.location = server.municipalityId
    ? { type: 'comune', municipalityId: server.municipalityId }
    : { type: 'none' };
  needsConfirmation = false;
  return { prefs: next, locationNeedsConfirmation: needsConfirmation };
}

/**
 * Anonymous-import candidate → PUT patch (v2 mirror of `buildAnonImportPatch`):
 * only fields the account never initialized, that carry provenance 'anon' and
 * hold a real value. The municipality id is a confirmed choice, so unlike a v1
 * city name it needs no readiness caveat on the client.
 */
export function buildAnonImportPatchV2(
  candidatePrefs: UserPreferencesV2,
  provenance: { location: 'anon' | 'local-only'; favoriteCategories: 'anon' | 'local-only' },
  initialized: ServerPrefsV2['initialized']
): V2PrefsPatch {
  const patch: V2PrefsPatch = {};
  if (!initialized.city && provenance.location === 'anon' && candidatePrefs.location.type === 'comune') {
    patch.municipalityId = candidatePrefs.location.municipalityId;
  }
  if (!initialized.radiusKm && provenance.location === 'anon') {
    patch.radiusKm = candidatePrefs.radiusKm;
  }
  if (
    !initialized.favoriteCategories &&
    provenance.favoriteCategories === 'anon' &&
    candidatePrefs.favoriteCategories.length > 0
  ) {
    patch.favoriteCategories = [...candidatePrefs.favoriteCategories];
  }
  return patch;
}

/** Authenticated fetch — same Bearer pattern as the v1 hook. */
async function meFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  const headers = new Headers(init.headers);
  if (!headers.has('Content-Type') && init.body) {
    headers.set('Content-Type', 'application/json');
  }
  if (token) headers.set('Authorization', `Bearer ${token}`);
  return fetch(path, { ...init, headers });
}

function patchIsEmpty(patch: V2PrefsPatch | null): boolean {
  return patch == null || Object.keys(patch).length === 0;
}

function mergePatch(base: V2PrefsPatch | null, next: V2PrefsPatch | null): V2PrefsPatch {
  return { ...(base ?? {}), ...(next ?? {}) };
}

/** An outbox entry (in-memory or persisted) pinned to one account + generation. */
interface PendingEntry {
  userId: string;
  gen: number;
  patch: V2PrefsPatch | null;
}

/** Same session ticket? A queued entry belongs to one account + generation only. */
function sameTicket(
  entry: PendingEntry | null | undefined,
  ticket: RequestTicket
): entry is PendingEntry {
  return entry != null && entry.userId === ticket.userId && entry.gen === ticket.gen;
}

/** Extract usable `candidates` from a 422 body; a malformed body yields none. */
function parseCandidates(raw: unknown): { city: string; province: string }[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  return raw
    .filter((c): c is { city: string; province: string } => {
      const o = c as Record<string, unknown>;
      return typeof o?.city === 'string' && typeof o?.province === 'string';
    })
    .map((c) => ({ city: c.city, province: c.province }));
}

export interface UserPrefsSyncV2Api {
  /** Queue an explicit v2 intent (per-user outbox, `ifRevision` flush). */
  sendPatch: (patch: V2PrefsPatch) => void;
  /** Explicit full reset: clear the comune, restore radius 25, clear categories. */
  resetPreferences: () => void;
  notice: PrefsSyncNotice | null;
  dismissNotice: () => void;
}

interface UseUserPrefsSyncV2Options {
  /** Hydrated v2 record content (null until the local store is ready). */
  prefs: UserPreferencesV2 | null;
  setPrefs: (updater: (prev: UserPreferencesV2) => UserPreferencesV2) => void;
  /** Current confirmation flag (the server outcome may change it). */
  needsConfirmation: boolean;
  setLocationNeedsConfirmation: (v: boolean) => void;
  /** Off while the discovery gate is in legacy/loading mode. */
  enabled?: boolean;
}

interface RequestTicket {
  userId: string;
  gen: number;
}

type PutOutcome =
  | { kind: 'applied'; server: ServerPrefsV2 }
  | { kind: 'conflict'; server: ServerPrefsV2 }
  | { kind: 'rejected'; notice: PrefsSyncNotice }
  | { kind: 'retryable' }
  | { kind: 'stale'; restore: boolean };

/**
 * Owns the v2 server revision, synchronization and mutations. Hydration is
 * never a write; explicit intents are queued per user and flushed only for
 * that user once a valid revision is known; responses of a previous account
 * are dropped by the session ticket.
 */
export function useUserPrefsSyncV2({
  prefs,
  setPrefs,
  needsConfirmation,
  setLocationNeedsConfirmation,
  enabled = false,
}: UseUserPrefsSyncV2Options): UserPrefsSyncV2Api {
  const { user } = useUserAuthOptional();
  const userId = user?.id ?? null;

  const [notice, setNotice] = useState<PrefsSyncNotice | null>(null);

  const prefsRef = useRef(prefs);
  prefsRef.current = prefs;
  const needsConfirmationRef = useRef(needsConfirmation);
  needsConfirmationRef.current = needsConfirmation;

  const genRef = useRef(0);
  const identityRef = useRef<RequestTicket | null>(null);
  const revisionRef = useRef<string | null>(null);
  const pendingRef = useRef<PendingEntry | null>(null);
  const inFlightGenRef = useRef<number | null>(null);
  const importDoneRef = useRef<number | null>(null);
  /** Only sync once the local v2 store is hydrated — the GET must merge INTO it. */
  const hydrated = prefs !== null;

  useEffect(() => {
    genRef.current += 1;
    revisionRef.current = null;
    importDoneRef.current = null;
    if (!enabled) {
      pendingRef.current = null;
      identityRef.current = null;
      return;
    }
    setNotice(null);
    if (userId) {
      const stored = loadV2Stored(userId);
      pendingRef.current = { userId, gen: genRef.current, patch: stored?.pending ?? null };
      identityRef.current = { userId, gen: genRef.current };
    } else {
      pendingRef.current = null;
      identityRef.current = null;
    }
  }, [userId, enabled]);

  const isStale = useCallback((ticket: RequestTicket): boolean => {
    const current = identityRef.current;
    return current == null || current.gen !== ticket.gen || current.userId !== ticket.userId;
  }, []);

  const pendingOf = useCallback((ticket: RequestTicket): V2PrefsPatch | null => {
    const current = pendingRef.current;
    if (!sameTicket(current, ticket)) return null;
    return current.patch;
  }, []);

  /** Apply authoritative server state to the local v2 record (never a write). */
  const applyState = useCallback(
    (ticket: RequestTicket, server: ServerPrefsV2) => {
      const pending = pendingOf(ticket);
      const local = prefsRef.current;
      if (!local) return;
      const merged = applyServerPrefsV2(server, local, pending, needsConfirmationRef.current);
      if (isStale(ticket)) return;
      setPrefs(() => merged.prefs);
      setLocationNeedsConfirmation(merged.locationNeedsConfirmation);
      syncPersonProperties({
        preferred_city: server.city,
        preferred_categories:
          server.favoriteCategories.length > 0 ? server.favoriteCategories : null,
        preferred_radius_km:
          server.radiusKm ?? (server.initialized.radiusKm ? DEFAULT_RADIUS_KM : null),
      });
    },
    [isStale, pendingOf, setPrefs, setLocationNeedsConfirmation]
  );

  const putPrefs = useCallback(
    async (
      ticket: RequestTicket,
      patch: V2PrefsPatch,
      ifRevision: string,
      mode: 'mutation' | 'import'
    ): Promise<PutOutcome> => {
      let res: Response;
      try {
        res = await meFetch('/api/me/preferences', {
          method: 'PUT',
          body: JSON.stringify({ ifRevision, ...patch }),
        });
      } catch {
        return isStale(ticket) ? { kind: 'stale', restore: true } : { kind: 'retryable' };
      }

      if (res.status === 409) {
        let server: ServerPrefsV2 | null = null;
        try {
          const body = (await res.json()) as { current?: unknown };
          server = parseServerPrefsV2(body?.current);
        } catch {
          server = null;
        }
        if (isStale(ticket)) return { kind: 'stale', restore: false };
        if (!server) return { kind: 'retryable' };
        return { kind: 'conflict', server };
      }

      if (res.status === 422) {
        let candidates: { city: string; province: string }[] | undefined;
        try {
          const body = (await res.json()) as { candidates?: unknown };
          candidates = parseCandidates(body?.candidates);
        } catch {
          candidates = undefined;
        }
        if (isStale(ticket)) return { kind: 'stale', restore: false };
        return {
          kind: 'rejected',
          notice: { kind: 'ambiguous-city', message: AMBIGUOUS_MESSAGE, candidates },
        };
      }

      if (res.status === 400) {
        if (isStale(ticket)) return { kind: 'stale', restore: false };
        return { kind: 'rejected', notice: { kind: 'rejected', message: REJECTED_MESSAGE } };
      }

      // 404 / 401 / 403 / 503 (writers maintenance) / 5xx: unapplied — keep the intent.
      if (res.status === 404 || res.status === 401 || res.status === 403 || !res.ok) {
        return isStale(ticket) ? { kind: 'stale', restore: true } : { kind: 'retryable' };
      }

      let server: ServerPrefsV2 | null = null;
      try {
        server = parseServerPrefsV2(await res.json());
      } catch {
        server = null;
      }
      if (isStale(ticket)) return { kind: 'stale', restore: false };
      if (!server) return { kind: 'retryable' };
      if (mode === 'mutation') {
        const preferredCategories = patch.favoriteCategories?.length
          ? patch.favoriteCategories
          : server.favoriteCategories.length > 0
            ? server.favoriteCategories
            : null;
        syncPersonProperties({
          preferred_city: server.city,
          preferred_categories: preferredCategories,
          preferred_radius_km: server.radiusKm ?? DEFAULT_RADIUS_KM,
        });
      }
      return { kind: 'applied', server };
    },
    [isStale]
  );

  /** Queue an intent on the ticket owner's record (v2 namespace outbox). */
  const queuePatch = useCallback(
    (ticket: RequestTicket, patch: V2PrefsPatch) => {
      if (isStale(ticket)) return;
      const current = pendingRef.current;
      if (sameTicket(current, ticket)) {
        current.patch = mergePatch(current.patch, patch);
      } else {
        pendingRef.current = {
          userId: ticket.userId,
          gen: ticket.gen,
          patch: mergePatch(null, patch),
        };
      }
      const existing = loadV2Stored(ticket.userId);
      if (existing) {
        saveV2Stored(ticket.userId, {
          ...existing,
          pending: mergePatch(existing.pending, patch),
        });
      } else if (prefsRef.current) {
        // No durable record yet (a conversion fault left the store unwritten):
        // seed it from the in-memory record so the queued intent and the
        // current prefs are persisted together instead of the defaults.
        // Provenance starts `local-only` (S7): nothing durable vouched for this
        // content, so it must never become importable at login — the
        // persistence effect overwrites it with the real provenance on the
        // same commit.
        saveV2Stored(ticket.userId, {
          v: 1,
          prefs: prefsRef.current,
          provenance: V2_LOCAL_ONLY_PROVENANCE,
          pending: mergePatch(null, patch),
          locationNeedsConfirmation: needsConfirmationRef.current,
        });
      }
    },
    [isStale]
  );

  const takePending = useCallback((ticket: RequestTicket): V2PrefsPatch | null => {
    const current = pendingRef.current;
    if (!current || current.userId !== ticket.userId || current.gen !== ticket.gen) return null;
    const taken = current.patch;
    if (patchIsEmpty(taken)) return null;
    current.patch = null;
    const stored = loadV2Stored(ticket.userId);
    if (stored) saveV2Stored(ticket.userId, { ...stored, pending: null });
    return taken;
  }, []);

  const restoreSent = useCallback(
    (ticket: RequestTicket, failed: V2PrefsPatch, sharedRef: boolean) => {
      const stored = loadV2Stored(ticket.userId);
      if (stored) {
        saveV2Stored(ticket.userId, {
          ...stored,
          pending: mergePatch(failed, stored.pending),
        });
      }
      if (!sharedRef || isStale(ticket)) return;
      const current = pendingRef.current;
      if (!sameTicket(current, ticket)) return;
      current.patch = mergePatch(failed, current.patch);
    },
    [isStale]
  );

  const flushPending = useCallback(
    async (ticket: RequestTicket) => {
      if (isStale(ticket)) return;
      if (inFlightGenRef.current === ticket.gen) return;
      const revision = revisionRef.current;
      if (revision == null) return;
      const sent = takePending(ticket);
      if (sent == null) return;

      inFlightGenRef.current = ticket.gen;
      let chain = false;
      try {
        const outcome = await putPrefs(ticket, sent, revision, 'mutation');
        if (outcome.kind === 'stale') {
          if (outcome.restore) restoreSent(ticket, sent, false);
          return;
        }
        switch (outcome.kind) {
          case 'applied':
            revisionRef.current = outcome.server.revision;
            applyState(ticket, outcome.server);
            chain = true;
            break;
          case 'conflict':
            revisionRef.current = outcome.server.revision;
            applyState(ticket, outcome.server);
            setNotice({ kind: 'conflict', message: CONFLICT_MESSAGE });
            break;
          case 'rejected':
            setNotice(outcome.notice);
            break;
          case 'retryable':
            restoreSent(ticket, sent, true);
            break;
        }
      } finally {
        if (inFlightGenRef.current === ticket.gen) inFlightGenRef.current = null;
      }
      if (chain && !isStale(ticket)) void flushPending(ticket);
    },
    [applyState, isStale, putPrefs, restoreSent, takePending]
  );

  /**
   * One-shot anonymous import, decided by the first successful GET. Returns
   * the server state to continue with (possibly the imported one), or `null`
   * when the account switched mid-import and the caller must abort.
   */
  const importAnonPrefs = useCallback(
    async (ticket: RequestTicket, server: ServerPrefsV2): Promise<ServerPrefsV2 | null> => {
      const candidate = loadV2Stored(null);
      if (!candidate?.provenance) {
        importDoneRef.current = ticket.gen;
        return server;
      }
      const patch = buildAnonImportPatchV2(
        candidate.prefs,
        candidate.provenance,
        server.initialized
      );
      if (patchIsEmpty(patch)) {
        importDoneRef.current = ticket.gen;
        claimV2AnonStored();
        return server;
      }
      const outcome = await putPrefs(ticket, patch, server.revision, 'import');
      if (outcome.kind === 'stale') return null;
      switch (outcome.kind) {
        case 'applied':
        case 'conflict':
          revisionRef.current = outcome.server.revision;
          importDoneRef.current = ticket.gen;
          claimV2AnonStored();
          if (outcome.kind === 'conflict') {
            setNotice({ kind: 'conflict', message: CONFLICT_MESSAGE });
          }
          return outcome.server;
        case 'rejected':
          importDoneRef.current = ticket.gen;
          claimV2AnonStored();
          setNotice(outcome.notice);
          return server;
        case 'retryable':
          return server;
      }
    },
    [putPrefs]
  );

  const runSync = useCallback(
    (ticket: RequestTicket) => {
      void (async () => {
        let server: ServerPrefsV2 | null = null;
        try {
          const res = await meFetch('/api/me/preferences');
          if (isStale(ticket)) return;
          if (res.status === 404 || res.status === 401 || res.status === 403 || !res.ok) return;
          server = parseServerPrefsV2(await res.json());
          if (isStale(ticket) || !server) return;
        } catch {
          return; // network failure — retried at the next trigger
        }

        revisionRef.current = server.revision;

        // One-shot anonymous import, decided by the first successful GET.
        if (importDoneRef.current !== ticket.gen) {
          const imported = await importAnonPrefs(ticket, server);
          if (imported == null) return; // account switched mid-import
          server = imported;
        }

        applyState(ticket, server);
        await flushPending(ticket);
      })();
    },
    [applyState, flushPending, importAnonPrefs, isStale]
  );

  // Sync triggers: hydration, login, tab visible again, network back.
  useEffect(() => {
    const identity = identityRef.current;
    if (!identity || !enabled || !hydrated) return;
    // The v2 record can APPEAR after this identity was established (the v1→v2
    // conversion writes the migrated outbox into it). Adopt that stored pending
    // before the first flush so a queued radius/favorites edit is not lost —
    // a queued LOCATION intent is already dropped by the conversion itself.
    const current = pendingRef.current;
    if (sameTicket(current, identity) && current.patch == null) {
      const stored = loadV2Stored(identity.userId);
      if (stored?.pending) current.patch = stored.pending;
    }
    const ticket: RequestTicket = { userId: identity.userId, gen: identity.gen };
    const trigger = () => runSync(ticket);
    trigger();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') trigger();
    };
    const onOnline = () => trigger();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('online', onOnline);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('online', onOnline);
    };
  }, [userId, enabled, hydrated, runSync]);

  const sendPatch = useCallback(
    (patch: V2PrefsPatch) => {
      const identity = identityRef.current;
      if (!identity || !prefsRef.current) return; // anonymous or not hydrated: local-only
      queuePatch(identity, patch);
      if (revisionRef.current == null) return;
      void flushPending(identity);
    },
    [flushPending, queuePatch]
  );

  const resetPreferences = useCallback(() => {
    sendPatch({ municipalityId: null, radiusKm: null, favoriteCategories: [] });
  }, [sendPatch]);

  const dismissNotice = useCallback(() => setNotice(null), []);

  return useMemo(
    () => ({ sendPatch, resetPreferences, notice, dismissNotice }),
    [sendPatch, resetPreferences, notice, dismissNotice]
  );
}
